import React, { useRef, useEffect, useState } from "react";
import GridContainer from "common/components/Grid/GridContainer.jsx";
import GridItem from "common/components/Grid/GridItem.jsx";
import withStyles from "@material-ui/core/styles/withStyles";
import SignaturePad from "react-signature-canvas";
import Button from "common/components/CustomButtons/Button.jsx";

function getSignatureLayout() {
  const isLandscape =
    window.matchMedia && window.matchMedia("(orientation: landscape)").matches;
  const viewportWidth = window.innerWidth || 360;
  const viewportHeight = window.innerHeight || 640;

  return {
    orientation: isLandscape ? "landscape" : "portrait",
    width: Math.min(viewportWidth * 0.92, isLandscape ? 900 : 560),
    height: Math.min(
      Math.max(viewportHeight * (isLandscape ? 0.52 : 0.34), 220),
      isLandscape ? 430 : 340
    ),
  };
}

const styles = {
  signatureShell: {
    width: "100%",
    margin: "0 auto",
    textAlign: "center",
  },
  signatureTitle: {
    margin: "0 0 10px",
    color: "#173963",
    fontSize: "18px",
    fontWeight: 700,
  },
  rotateHint: {
    margin: "0 0 14px",
    color: "#607187",
    fontSize: "13px",
    lineHeight: 1.45,
  },
  errorMessage: {
    margin: "0 0 14px",
    color: "#b33a3a",
    fontWeight: 600,
  },
  sigContainer: {
    backgroundColor: "white",
    border: "2px solid #173963",
    borderRadius: "6px",
    boxShadow: "0 10px 24px rgba(23, 57, 99, 0.12)",
    overflow: "hidden",
    touchAction: "none",
  },
  sigCanvas: {
    display: "block",
    width: "100%",
    height: "100%",
    cursor: "crosshair",
    touchAction: "none",
  },
  actions: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "12px",
    marginTop: "18px",
  },
  buttonStyle: {
    margin: 0,
  },
};
/*
function dataURItoBlob(dataURI) {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  var byteString = atob(dataURI.split(",")[1]);

  // separate out the mime component
  var mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

  // write the bytes of the string to an ArrayBuffer
  var ab = new ArrayBuffer(byteString.length);
  var ia = new Uint8Array(ab);
  for (var i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  //Old Code
  //write the ArrayBuffer to a blob, and you're done
  //var bb = new BlobBuilder();
  //bb.append(ab);
  //return bb.getBlob(mimeString);

  //New Code
  return new Blob([ab], { type: mimeString });
}
*/
const Signature = (props) => {
  //console.log(window.innerHeight);
  const signatureRef = useRef(null);
  const initialLayout = getSignatureLayout();
  const [orientation, setOrientation] = useState(initialLayout.orientation);
  const [padHeight, setPadHeight] = useState(initialLayout.height);
  const [padWidth, setPadWidth] = useState(initialLayout.width);
  const [hasSignature, setHasSignature] = useState(false);
  const signatureImageRef = useRef(null);
  const [submitError, setSubmitError] = useState("");
  const { classes, mainPanelRef } = props;

  useEffect(() => {
    function updateLayout() {
      const nextLayout = getSignatureLayout();
      setOrientation(nextLayout.orientation);
      setPadHeight(nextLayout.height);
      setPadWidth(nextLayout.width);
    }

    window.addEventListener("orientationchange", updateLayout);
    window.addEventListener("resize", updateLayout);
    if (mainPanelRef != null) {
      mainPanelRef.scrollTop = 0;
    }

    if (window?.screen?.orientation?.lock) {
      try {
        const lockResult = window.screen.orientation.lock("landscape");
        if (lockResult?.catch) {
          lockResult.catch(() => null);
        }
      } catch (e) {
        // Orientation lock is optional and may be blocked outside fullscreen.
      }
    }

    return () => {
      window.removeEventListener("orientationchange", updateLayout);
      window.removeEventListener("resize", updateLayout);
      if (window?.screen?.orientation?.unlock) {
        try {
          window.screen.orientation.unlock();
        } catch (e) {
          // Ignore browsers that reject unlock when no lock was applied.
        }
      }
    };
  }, [mainPanelRef]);

  useEffect(() => {
    if (signatureImageRef.current && signatureRef.current) {
      signatureRef.current.clear();
      signatureRef.current.fromDataURL(signatureImageRef.current, {
        width: padWidth,
        height: padHeight,
      });
    }
  }, [padHeight, padWidth]);

  function handleSignatureEnd() {
    if (signatureRef.current?.isEmpty()) {
      return;
    }

    setHasSignature(true);
    setSubmitError("");
    signatureImageRef.current = signatureRef.current.toDataURL("image/png");
  }

  async function handleSubmit() {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setSubmitError("Please sign before continuing.");
      return;
    }

    props.setSignature(signatureRef.current.toDataURL("image/png"));
    props.goToReview();
    return;
    //console.log(canvasRef.current.getSaveData());
    /*
    const storeState = store.getState();
    const endPoint = `user.${storeState.services.ids.user}/contract`;

    //URL built from config file and specified end point (target)
    let url = `${config.API.ADDRESS}?target=${endPoint}&docpost=t&term=${props.term}`;

    const formData = new FormData();
    formData.append(
      "file",
      dataURItoBlob(signatureRef.current.toDataURL("image/png"))
    );

    //myInit is the second fetch() argument used to specify methods, headers, body...
    let myInit: init = {
      method: "post",
      headers: {
        "x-omicron": storeState.services.api.JRR,
      },
      body: formData,
    };

    var res = await fetch(url, myInit);
    var resjson = await res.json();

    const tempcontractid = resjson.list[0].tempcontractid;
    props.setTempContractId(tempcontractid);
    props.goToReview();
    */
  }
  function handleClear() {
    signatureRef.current.clear();
    setHasSignature(false);
    signatureImageRef.current = null;
    setSubmitError("");
  }

  const showRotateHint = orientation === "portrait" && padWidth < 620;

  return (
    <GridContainer justify="center">
      <GridItem xs={12}>
        <div
          className={classes.signatureShell}
          style={{ maxWidth: `${padWidth}px` }}
        >
          <h4 className={classes.signatureTitle}>Sign inside the box</h4>
          {showRotateHint ? (
            <p className={classes.rotateHint}>
              Rotate your phone for a larger signing area.
            </p>
          ) : null}
          {submitError ? (
            <p className={classes.errorMessage}>{submitError}</p>
          ) : null}
          <div
            style={{
              height: `${padHeight}px`,
            }}
            className={classes.sigContainer}
          >
            <SignaturePad
              ref={signatureRef}
              penColor="blue"
              minWidth={0.8}
              maxWidth={2.4}
              onEnd={handleSignatureEnd}
              canvasProps={{
                className: classes.sigCanvas,
                "aria-label": "Signature pad",
              }}
            />
          </div>
          <div className={classes.actions}>
            <Button
              className={classes.buttonStyle}
              color="default"
              disabled={false}
              onClick={props.goToContract}
            >
              Back
            </Button>
            <Button
              className={classes.buttonStyle}
              color="default"
              disabled={false}
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button
              className={classes.buttonStyle}
              color="primary"
              disabled={!hasSignature}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
      </GridItem>
    </GridContainer>
  );
  /*

    return (
      <GridContainer justify="center">
        <GridItem xs={12} md={10} lg={8}>
          <div className={classes.sigContainer}>
            <SignaturePad
              ref={signatureRef}
              penColor="black"
              canvasProps={{
                className: classes.sigCanvas,
              }}
            />
          </div>
        </GridItem>
        <GridItem xs={12} md={10} lg={8}>
          <GridContainer align="center" justify="center">
            <GridItem xs={12} sm={6}>
              <Button
                className={classes.buttonStyle}
                color="default"
                disabled={false}
                onClick={handleClear}
              >
                Clear
              </Button>
            </GridItem>
            <GridItem xs={12} sm={6}>
              <Button
                className={classes.buttonStyle}
                color="primary"
                disabled={false}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </GridItem>
          </GridContainer>
        </GridItem>
      </GridContainer>
    );
    */
};

export default withStyles(styles)(Signature);
